// Iter-f3 — Brand Service Performance · Branch detail (7 charts).
//
// Opened from a branch card on /staff/brand/service-performance.
// Renders 7 interactive charts (counts on every bar / slice) powered
// entirely by the server aggregate at
// `GET /reports/brand/service-performance/detail/{bid}`.
//
// Period A vs Period B custom ranges arrive as query params so the
// page is deep-linkable and refresh-safe.
//
// No engine files are imported or edited.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Download } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, Legend, LineChart, Line, PieChart, Pie, Cell,
  LabelList,
} from "recharts";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { isOwner } from "@/lib/AuthEngine";
import { todayBali } from "@/lib/ReportingEngine";
import { useDashboardSnapshot, buildRangeSnapshotFilename } from "@/lib/NotificationEngine";
import { InfoHint } from "@/components/common/InfoHint";
import { SERVICE_PERFORMANCE_TIPS } from "@/lib/reportTips";

const SERIES_COLORS = ["#0284c7", "#16a34a", "#d97706", "#dc2626", "#7c3aed", "#0891b2", "#be185d"];

export default function BrandServicePerformanceDetail() {
  const { bid } = useParams();
  const nav = useNavigate();
  const { staff } = useAuth();
  const [sp] = useSearchParams();
  const today = todayBali();

  const [aFrom, setAFrom] = useState(sp.get("a_from") || today);
  const [aTo,   setATo]   = useState(sp.get("a_to")   || today);
  const [bFrom, setBFrom] = useState(sp.get("b_from") || today);
  const [bTo,   setBTo]   = useState(sp.get("b_to")   || today);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  const [tick, setTick] = useState(false);
  useEffect(() => {
    let cancelled = false;
    staffApi.get("/report-permissions/me", { params: { scope: "brand" } })
      .then((r) => {
        if (cancelled) return;
        const keys = new Set(r.data?.allowed_keys || []);
        setTick(keys.has("service_performance"));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const canSee = isOwner(staff) || tick;

  const params = useMemo(() => ({
    a_from: aFrom, a_to: aTo, b_from: bFrom, b_to: bTo,
  }), [aFrom, aTo, bFrom, bTo]);

  const load = useCallback(() => {
    if (!canSee || !bid) return;
    setLoading(true);
    staffApi.get(`/reports/brand/service-performance/detail/${bid}`, { params })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [canSee, bid, params]);
  useEffect(() => { load(); }, [load]);

  const branchLabel = data?.branch_name || "Branch";
  const { downloadRef, downloadPng, capturing } = useDashboardSnapshot({
    label: `Service Performance ${branchLabel}`,
    dateFrom: aFrom,
    dateTo: aTo,
    filename: buildRangeSnapshotFilename(
      `Service Performance ${branchLabel}`, aFrom, aTo, bFrom, bTo,
    ),
  });

  return (
    <StaffShell context="brand">
      <div className="flex items-center gap-2 mb-4" data-html2canvas-ignore="true">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav("/staff/brand/service-performance")}
          data-testid="brand-sp-detail-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
      <div ref={downloadRef} className="bg-background">
      <div className="flex items-end justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="brand-sp-detail-title">
            {data?.branch_name || "…"}
          </h1>
          <p className="text-xs font-medium text-sky-600 mt-1">
            Period A · {aFrom} → {aTo} &nbsp;vs&nbsp; Period B · {bFrom} → {bTo}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={downloadPng}
          disabled={capturing || loading}
          className="gap-1 h-9 shrink-0"
          data-testid="brand-sp-detail-download"
          data-html2canvas-ignore="true"
        >
          <Download className={`w-4 h-4 ${capturing ? "animate-pulse" : ""}`} />
          {capturing ? "Capturing…" : "Download"}
        </Button>
      </div>

      {!canSee && (
        <Card className="rounded-lg mb-6">
          <CardContent className="p-8 text-sm text-muted-foreground text-center">
            You don't have permission to view the Service Performance dashboard.
          </CardContent>
        </Card>
      )}

      {canSee && (
        <>
          <Card className="rounded-lg mb-4">
            <CardContent className="p-3 flex items-center gap-3 flex-wrap" data-testid="brand-sp-detail-filters">
              <RangePicker label="Period A" from={aFrom} to={aTo} onFrom={setAFrom} onTo={setATo} testIdPrefix="brand-sp-detail-a" />
              <span className="h-6 w-px bg-border mx-1" />
              <RangePicker label="Period B" from={bFrom} to={bTo} onFrom={setBFrom} onTo={setBTo} testIdPrefix="brand-sp-detail-b" />
              <Button
                size="sm"
                variant="outline"
                onClick={load}
                disabled={loading}
                className="gap-1 ml-auto"
                data-testid="brand-sp-detail-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </CardContent>
          </Card>

          <SectionHeading>Section 1 · Single Treatments</SectionHeading>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <ChartCard title="Top 20 Treatments · A vs B" testId="brand-sp-chart-top20-singles" tooltip={SERVICE_PERFORMANCE_TIPS.treatments}>
              <TopHorizontalBar rows={data?.top20_singles || []} loading={loading} />
            </ChartCard>
            <ChartCard title={`Treatment Trend (top 5) · by ${data?.bucket_mode || "week"}`} testId="brand-sp-chart-trend-singles" tooltip={SERVICE_PERFORMANCE_TIPS.treatments}>
              <TrendLines rows={data?.singles_trend || []} series={data?.top5_singles || []} loading={loading} />
            </ChartCard>
          </div>

          <SectionHeading>Section 2 · Packages</SectionHeading>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <ChartCard title="Top 20 Packages · A vs B" testId="brand-sp-chart-top20-packages" tooltip={SERVICE_PERFORMANCE_TIPS.treatments}>
              <TopHorizontalBar rows={data?.top20_packages || []} loading={loading} />
            </ChartCard>
            <ChartCard title={`Package Trend (top 5) · by ${data?.bucket_mode || "week"}`} testId="brand-sp-chart-trend-packages" tooltip={SERVICE_PERFORMANCE_TIPS.treatments}>
              <TrendLines rows={data?.packages_trend || []} series={data?.top5_packages || []} loading={loading} />
            </ChartCard>
          </div>

          <SectionHeading>Section 3 · Combined</SectionHeading>
          <div className="grid lg:grid-cols-2 gap-4 mb-6">
            <ChartCard title="Bookings by Hour · A vs B" testId="brand-sp-chart-hour" tooltip={SERVICE_PERFORMANCE_TIPS.treatments}>
              <HourSplit rows={data?.hour_split || []} loading={loading} />
            </ChartCard>
            <ChartCard title="Bookings by Weekday · A vs B" testId="brand-sp-chart-weekday" tooltip={SERVICE_PERFORMANCE_TIPS.treatments}>
            <WeekdayDualPie
              rows={data?.weekday_split || []}
              loading={loading}
              labelA={`Period A · ${aFrom} → ${aTo}`}
              labelB={`Period B · ${bFrom} → ${bTo}`}
            />
            </ChartCard>
            <ChartCard title="Top 10 Practitioners · A vs B" testId="brand-sp-chart-top10-practitioners" className="lg:col-span-2" tooltip={SERVICE_PERFORMANCE_TIPS.treatments}>
              <TopHorizontalBar rows={data?.top10_practitioners || []} loading={loading} />
            </ChartCard>
          </div>
        </>
      )}
      </div>
    </StaffShell>
  );
}

function RangePicker({ label, from, to, onFrom, onTo, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2">
      <span className="tracking-overline text-[10px] text-muted-foreground">{label}</span>
      <Input type="date" value={from} onChange={(e) => onFrom(e.target.value)} className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-from`} />
      <span className="text-xs text-muted-foreground">to</span>
      <Input type="date" value={to}   onChange={(e) => onTo(e.target.value)}   className="h-8 w-40 text-xs" data-testid={`${testIdPrefix}-to`} />
    </div>
  );
}

function SectionHeading({ children }) {
  return <p className="tracking-overline text-[10px] text-muted-foreground mb-2">{children}</p>;
}

function ChartCard({ title, children, testId, className = "", tooltip }) {
  return (
    <Card className={`rounded-lg relative ${className}`} data-testid={testId}>
      <InfoHint text={tooltip} testId={`${testId}-info`} />
      <CardContent className="p-4">
        <p className="text-sm font-medium mb-3 pr-5">{title}</p>
        <div className="h-[360px]">{children}</div>
      </CardContent>
    </Card>
  );
}

/* -------------------- Chart components -------------------- */

function TopHorizontalBar({ rows, loading }) {
  if (loading) return <Centered>Loading…</Centered>;
  if (!rows.length) return <Centered>No data in Period A.</Centered>;
  // Longer labels crowd the Y axis — keep card tall enough to fit.
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 48, bottom: 8, left: 12 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
        <YAxis type="category" dataKey="label" width={160} tick={{ fontSize: 11 }} />
        <ChartTooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="count_a" name="Period A" fill="#0284c7" radius={[0, 3, 3, 0]} barSize={14}>
          <LabelList dataKey="count_a" position="right" fontSize={11} />
        </Bar>
        <Bar dataKey="count_b" name="Period B" fill="#94a3b8" radius={[0, 3, 3, 0]} barSize={8}>
          <LabelList dataKey="count_b" position="right" fontSize={10} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function TrendLines({ rows, series, loading }) {
  if (loading) return <Centered>Loading…</Centered>;
  if (!rows.length || !series.length) return <Centered>No data in Period A.</Centered>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="bucket" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
        <ChartTooltip />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        {series.map((s, i) => (
          <Line key={s} type="monotone" dataKey={s} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function HourSplit({ rows, loading }) {
  if (loading) return <Centered>Loading…</Centered>;
  if (!rows.length) return <Centered>No bookings in range.</Centered>;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickFormatter={(h) => `${String(h).padStart(2, "0")}:00`} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
        <ChartTooltip labelFormatter={(h) => `${String(h).padStart(2, "0")}:00`} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="a" name="Period A" fill="#0284c7" radius={[2, 2, 0, 0]}>
          <LabelList dataKey="a" position="top" fontSize={9} />
        </Bar>
        <Bar dataKey="b" name="Period B" fill="#94a3b8" radius={[2, 2, 0, 0]}>
          <LabelList dataKey="b" position="top" fontSize={9} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function WeekdayDualPie({ rows, loading, labelA, labelB }) {
  if (loading) return <Centered>Loading…</Centered>;
  const panels = [
    { key: "a", label: labelA, dataKey: "count" },
    { key: "b", label: labelB, dataKey: "count_b" },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 h-full">
      {panels.map((p) => {
        const total = rows.reduce((s, r) => s + (r[p.dataKey] || 0), 0);
        return (
          <div key={p.key} className="flex flex-col" data-testid={`brand-sp-chart-weekday-${p.key}`}>
            <p className="text-[11px] font-medium text-center text-muted-foreground mb-1">{p.label}</p>
            <div className="flex-1 min-h-0">
              {total > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <ChartTooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Pie
                      data={rows}
                      dataKey={p.dataKey}
                      nameKey="weekday"
                      cx="50%"
                      cy="50%"
                      outerRadius={62}
                      label={(e) => (e[p.dataKey] > 0 ? `${e.weekday} · ${e[p.dataKey]}` : "")}
                      labelLine={false}
                      isAnimationActive={false}
                    >
                      {rows.map((_, i) => (
                        <Cell key={i} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Centered>No bookings in this period.</Centered>
              )}
            </div>
            <p className="text-[11px] text-center text-muted-foreground" data-testid={`brand-sp-chart-weekday-${p.key}-total`}>
              Total {total.toLocaleString("en-US")}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="h-full flex items-center justify-center text-xs text-muted-foreground">{children}</div>
  );
}
